import {
  Alert, Box, Button, FormControl, FormHelperText, InputLabel,
  MenuItem, Select, TextField,
} from "@mui/material";
import { loadProfileActive } from "./config/btm";
import { useIsGreek, useTranslateText } from "../context/LanguageContext";

const fieldSx = { flex: { xs: "1 1 100%", md: "0 0 30%" }, minWidth: 0 };

export function DemandProfileType({ config, onChange }) {
  const isGreek = useIsGreek();
  const label = isGreek ? "Πρότυπο αρχικών ρυθμίσεων" : "Starting settings preset";
  // A measured profile replaces the generated load shape. Retain the saved
  // use case when hidden, so switching demand off/on does not erase it.
  if (!config?.feasibility?.technical?.demand?.enabled || loadProfileActive(config)) return null;
  return (
    <Box sx={fieldSx}>
      <FormControl fullWidth>
        <InputLabel id="demand-profile-type-label">{label}</InputLabel>
        <Select
          labelId="demand-profile-type-label"
          label={label}
          value={config.useCase || "Commercial"}
          onChange={(event) => onChange(event.target.value)}
        >
          <MenuItem value="Commercial">Commercial</MenuItem>
          <MenuItem value="Residential Rural">Residential Rural</MenuItem>
          <MenuItem value="Residential Urban">Residential Urban</MenuItem>
        </Select>
        <FormHelperText>
          {isGreek
            ? "Η αλλαγή προτύπου επαναφέρει την κατανομή ημερών, τη χρήση ωραρίου, τα ημερήσια προφίλ, τις αργίες και τη διακύμανση στις αρχικές ρυθμίσεις του. Οι καταχωρισμένες kWh και ώρες λειτουργίας διατηρούνται. Σε διμηνιαία, τριμηνιαία ή ετήσια κατανάλωση, το πρότυπο επηρεάζει και την κατανομή στους μήνες."
            : "Changing preset resets day allocation, schedule usage, daily profiles, holidays and variation to its defaults. Entered kWh and operating hours are retained. For two-month, quarterly or annual totals, the preset also affects the allocation between months."}
        </FormHelperText>
      </FormControl>
    </Box>
  );
}

export function OperationalTimeStep({ value, onChange }) {
  // Displayed in a read-only input, whose text is a property the DOM
  // translator cannot reach; it asks for its own Greek.
  const translateText = useTranslateText();
  // Absent values retain the engine's established 15-minute behaviour.
  // Explicit invalid/legacy values remain visible until the user corrects them.
  const supported = value === undefined || (
    (typeof value === "number" || (
      typeof value === "string" && /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(value.trim())
    )) && Number(value) === 15
  );
  return (
    <Box sx={fieldSx}>
      <TextField
        fullWidth
        label="Operational Time Step"
        value={
          supported
            ? translateText("15 minutes (fixed)")
            // The value is the user's own, so only the suffix is translated;
            // interpolating it into a sentence would make the whole thing
            // unmatchable in a catalogue keyed on whole strings.
            : `${value ?? translateText("Not set")} ${translateText("(unsupported)")}`
        }
        InputProps={{ readOnly: true }}
        error={!supported}
        helperText="Daily optimisation and bid schedules use 15-minute intervals. Feasibility studies use hourly intervals."
      />
      {!supported && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          This saved time step is unsupported for operational optimisation.
          Update it to 15 minutes and save the project before running optimisation.
          <Button size="small" onClick={() => onChange("15")} sx={{ mt: 0.5 }}>
            Use 15 minutes
          </Button>
        </Alert>
      )}
    </Box>
  );
}
